import React, { FC, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';
import { AttentionMessage } from '@eika/attention-message';
import { InfoCard } from '@eika/card';
import { LinkButton, StandardButton } from '@eika/button';
import { Link } from '@eika/link';
import { ErrorResponse } from '@eika-infrastruktur/request';
import { useSelector } from 'react-redux';
import { IconTypes } from '@eika/icon';
import AttentionMessageTypes from '@eika/attention-message/src/AttentionMessageTypes';
import { RouterConfig } from '../../../../../app/Router';
import Grid from '../../../../../common/Grid/Grid';
import { Spinner } from '../../../../../common/share/ShareComponents';
import { getPageAppending } from '../../../../../common/util/Functions';
import './ConfirmSwitchPage.scss';
import { State } from '../../../../../domain/State';
import { useStorebandInfo } from '../../../../../common/util/Hook';
import Card from '../../../../../common/Card/Card';
import SetDownscalingModal from './SetDownscalingModal';
import { SelectableProfile, SwitchStorebrandSavingsProfileResponseApi } from '../../../../../domain/Storebrand';
import SelectableProfileFundAllocation from '../switch/SelectableProfileFundAllocation';
import SwitchSuccess from './SwitchSuccess';
import NothingChangedModal from './NothingChangedModal';
import { useTranslate } from '../../../../../hooks/useTranslate';
import { PensionProduct } from '../../types';

type Props = RouteComponentProps<{ id?: string; isin?: string }> & RouterConfig;
const ConfirmSwitchPage: FC<Props> = (props) => {
    const [error, setError] = useState('');
    const [step, setStep] = useState('form');
    const [downscaling, setDownscaling] = useState(true);
    const [modalOpen, setModalOpen] = useState<false | 'downscaling' | 'not-changed'>(false);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);
    const appending = useMemo(() => getPageAppending(props), []);

    const translate = useTranslate();

    const storebrandState = useSelector((state: State) => state?.Storebrand);
    const pensionState = useSelector((state: State) => state?.Pensjon);

    const productDescription = useMemo(
        () =>
            pensionState?.arbeidsgiversPensjonsSparing?.find(
                (product: PensionProduct) => product.agreementNumber === props.match.params.id
            ).productDescription,
        [pensionState, props.match.params.id]
    );

    const agreementInfo = useMemo(
        () => storebrandState?.info?.[props.match?.params.id || ''],
        [storebrandState, props.match?.params.id]
    );

    const contractNumber = useMemo(
        () => storebrandState?.info?.[props.match?.params.id || ''].contractNumber,
        [storebrandState, props.match?.params.id]
    );

    const currentProfile = useMemo(() => {
        const name = agreementInfo?.riskProfiles.join(', ');
        const fundAllocationPercentage = agreementInfo?.selectableProfiles.find(
            (profile: SelectableProfile) => profile.name === name
        )?.fundAllocationPercentage;
        return { name, fundAllocationPercentage };
    }, [agreementInfo]);

    const targetProfile = useMemo(
        () =>
            agreementInfo?.selectableProfiles?.find(
                (profile: SelectableProfile) => profile.isin === props.match?.params.isin
            ),
        [agreementInfo, props.match?.params.isin]
    );

    const forceDownscalingOff = useMemo(
        () => agreementInfo?.riskReductionProfile?.isin === targetProfile?.isin,
        [targetProfile, agreementInfo]
    );

    const confirmChange = () => {
        if (agreementInfo?.riskProfiles.length === 1 && agreementInfo?.riskProfiles[0] === targetProfile.name) {
            setModalOpen('not-changed');
            return;
        }

        setSwitching(true);
        setError('');
        props.action
            .switchStorebrandSavingsProfile({
                contractNumber,
                selectedProfileIsin: props.match.params.isin,
                automaticDownscaling: forceDownscalingOff ? false : downscaling,
            })
            .then((response: SwitchStorebrandSavingsProfileResponseApi) => {
                if (response.success) {
                    setStep('success');
                } else {
                    setError('fail');
                }
            })
            .catch((error: ErrorResponse) => {
                setError(error.message);
            })
            .finally(() => {
                setSwitching(false);
            });
    };

    useStorebandInfo({ Storebrand: storebrandState }, props.action);

    useEffect(() => {
        if (agreementInfo) setLoading(false);
    }, [agreementInfo]);

    const currentProfileCard = (
        <Card
            namespace="current-profile-card"
            data={{
                textList: [
                    [
                        {
                            element: (
                                <div>
                                    <article className="-title">Endre fra:</article>
                                    <article className="-name">{currentProfile?.name}</article>
                                    <SelectableProfileFundAllocation
                                        fundAllocationPercentage={currentProfile?.fundAllocationPercentage}
                                    />
                                </div>
                            ),
                        },
                    ],
                ],
            }}
            type="list-item"
        />
    );

    const targetProfileCard = (
        <Card
            namespace="target-profile-card"
            data={{
                textList: [
                    [
                        {
                            element: (
                                <Link
                                    icon={IconTypes.LITEN_PIL_HOEYRE}
                                    iconOnRightSide
                                    href={`${window.location.href.split('/').slice(0, -2).join('/')}/switch`}
                                >
                                    <div>
                                        <article className="-title">Endre til:</article>
                                        <article className="-name">{targetProfile?.name}</article>
                                        <SelectableProfileFundAllocation
                                            fundAllocationPercentage={targetProfile?.fundAllocationPercentage}
                                        />
                                    </div>
                                </Link>
                            ),
                        },
                    ],
                ],
            }}
            type="list-item"
        />
    );

    const dowscalingCard = (
        <Card
            namespace="downscaling-card"
            data={{
                textList: [
                    [
                        {
                            element: (
                                <div>
                                    <div className='card-row'>
                                        <article>{translate('automaticDownscaling')}</article>
                                        {forceDownscalingOff ? (
                                            <div>{translate('no')}</div>
                                        ) : (
                                            <LinkButton
                                                icon={IconTypes.PENN_24}
                                                iconOnRightSide
                                                onClick={() => setModalOpen('downscaling')}
                                            >
                                                {downscaling ? translate('yes') : translate('no')}
                                            </LinkButton>
                                        )}
                                    </div>
                                    {forceDownscalingOff && (
                                        <AttentionMessage
                                            type={AttentionMessageTypes.INFO}
                                            text={translate('forcedDownscalingOff')}
                                            icon={IconTypes.INFO_24}
                                        />
                                    )}
                                </div>
                            ),
                        },
                    ],
                ],
            }}
            type="list-item"
        />
    );

    return (
        <div className={`confirm-switch ${appending || 'none-appending'}`}>
            <Grid
                namespace="sparing-content"
                list={[
                    {
                        layout: 'eight',
                        element: (
                            <>
                                {loading && <Spinner type="linkPanel" />}
                                {!loading && (
                                    <>
                                        {step === 'success' ? (
                                            <SwitchSuccess fundName={targetProfile.name} />
                                        ) : (
                                            <div>
                                                <div className="only-for-mobile">
                                                    <h1>{translate('reviewAndChange')}</h1>
                                                </div>
                                                <InfoCard
                                                    asHeader
                                                    className="sgw-layout-item sgw-layout-item__full-width"
                                                    img={<RichIcon type={RichIconTypes.VEKT_LAV_RISIKO} />}
                                                >
                                                    <div className="only-for-desktop">
                                                        <InfoCard.TitleH1>
                                                            {translate('reviewAndChange')}
                                                        </InfoCard.TitleH1>
                                                        <p className="typography-paragraph">
                                                            {translate('changeProfile')}
                                                        </p>
                                                    </div>
                                                    <div className="only-for-mobile">
                                                        <h2>{translate('changeProfile')}</h2>
                                                    </div>
                                                    <p className="typography-paragraph">{productDescription}</p>
                                                </InfoCard>

                                                <div className="confirm-switch-content">
                                                    {switching ? (
                                                        <Spinner />
                                                    ) : (
                                                        <>
                                                            {currentProfileCard}
                                                            {targetProfileCard}
                                                            {dowscalingCard}
                                                        </>
                                                    )}

                                                    {error && (
                                                        <AttentionMessage
                                                            type={AttentionMessageTypes.ERROR}
                                                            text={translate('ops')}
                                                            icon={IconTypes.KRYSS_DYN}
                                                        />
                                                    )}
                                                    {!switching && (
                                                        <div className="button-row">
                                                            <Link
                                                                // remove two url segments
                                                                href={location.href.split('/').slice(0, -2).join('/')}
                                                            >
                                                                {translate('cancel')}
                                                            </Link>
                                                            <StandardButton
                                                                size="small"
                                                                buttonType="positive"
                                                                onClick={confirmChange}
                                                                placeholder="Bekreft endring"
                                                            >
                                                                {translate('confirmChange')}
                                                            </StandardButton>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        ),
                    },
                ]}
            />
            <SetDownscalingModal
                downscalingState={[downscaling, setDownscaling]}
                open={modalOpen === 'downscaling'}
                onClose={() => setModalOpen(false)}
            />
            <NothingChangedModal open={modalOpen === 'not-changed'} onClose={() => setModalOpen(false)} />
        </div>
    );
};
export default ConfirmSwitchPage;
